/**
 * WONDER, drawn as a constellation.
 *
 * Each glyph is built from open strokes with deliberate gaps and a lit point
 * at every stroke end, so the word reads as points joined by light rather than
 * as a typeface. Glyphs are authored in their own box -- 0..width across,
 * 0..CAP down -- and the word is laid out by accumulating those widths, so a
 * glyph can be redrawn or resized without any chance of it landing on top of
 * its neighbour. An earlier pass hand-assigned the x offsets and the O and N
 * ended up sharing the same space.
 */
export const CAP = 175;

/**
 * Space between glyphs, in the same units as the widths.
 *
 * Measured off the artwork rather than chosen. Every glyph box carries 4 units
 * of padding on each side, so the space between two stroke centrelines is
 * `GAP + 8`; the design sets that at about 37, which puts this at 29. The
 * result is a word 947 units wide against a 175 cap -- the artwork's own
 * 5.4 ratio.
 */
const GAP = 29;

/** Padding so stroke caps, dots and their bloom are not clipped at the edges. */
const PAD = 26;

const GLYPHS = [
  {
    id: 'W',
    width: 180,
    // Traced off the Wonder mark itself (`src/assets/images/join_wonder_logo.svg`),
    // because this glyph and the logo are the same letter and a reader sees
    // them within a scroll of each other.
    //
    // Read the mark as two strokes rather than five, which is how it is drawn:
    //
    //   a checkmark  the left stem falls to the baseline and turns back up to
    //                the right, ending in open space just past halfway
    //   a caret      a free foot rises to the apex, falls to the baseline, and
    //                turns straight up into the right stem
    //
    // Measured as fractions of the span between the stems and of the cap:
    //
    //   checkmark tip   0.510 across, 0.424 down  ->  (92, 74)
    //   caret foot      0.194 across, 0.974 down  ->  (37, 170)
    //   apex            0.600 across, 0.510 down  -> (107, 89)
    //
    // The apex sits right of the midpoint of the stems, not on it, and the
    // caret's two arms span an equal 0.406 either side of it.
    //
    // Both stems run the full cap, and that is the correction that matters
    // most. The mark is a filled outline, so its corners are mitred and the
    // painted tip of each stem carries on past where the centrelines cross --
    // the centrelines meet at 0.93 of the cap, the paint reaches 0.99. Copying
    // the centreline figure is what left an earlier pass with both stems
    // hanging a few units clear of the baseline while the caret's free foot
    // sat below them, so the letter read as floating with one toe down. A
    // stroke this thin has no mitre to make up the difference: it has to be
    // drawn to where the logo's paint actually ends.
    strokes: [
      'M 4 0 L 4 175',
      'M 4 175 L 92 74',
      'M 37 170 L 107 89',
      'M 107 89 L 176 175',
      'M 176 0 L 176 175',
    ],
    nodes: [
      [4, 0],
      [4, 175],
      [37, 170],
      [92, 74],
      [107, 89],
      [176, 0],
      [176, 175],
    ],
  },
  {
    id: 'O',
    width: 183,
    // Two circles, one inside the other at 0.77 of its radius, each broken
    // once and on opposite sides -- the outer opens to the right, the inner to
    // the left. So the top and bottom of the letter are drawn twice, the right
    // only by the inner ring and the left only by the outer, and the four lit
    // points are the four arc ends rather than waypoints on a curve.
    //
    // True circles, radius 87.5 about (91.5, 87.5) -- the full cap tall and
    // therefore the full cap wide, which makes this the widest letter in the
    // word by some way. That is correct: an O narrowed to sit closer to its
    // neighbours reads as a squashed oval long before it reads as economical,
    // and the two gaps already cost the silhouette some of its roundness
    // without help from the radii.
    //
    // Each arc takes the long way between its ends -- large-arc flag 1 -- and
    // they sweep opposite ways, the outer counter-clockwise and the inner
    // clockwise, which is what puts the two gaps on opposite sides.
    strokes: ['M 119.1 4.5 A 87.5 87.5 0 1 0 119.1 170.5', 'M 47.1 37.3 A 67 67 0 1 1 51.7 141.4'],
    nodes: [
      [119.1, 4.5],
      [119.1, 170.5],
      [47.1, 37.3],
      [51.7, 141.4],
    ],
  },
  {
    id: 'N',
    width: 127,
    // The diagonal is doubled, the way the W's peak and the R's leg are.
    //
    // The second run is exactly parallel to the first -- both ends measure
    // 16.5 units off it, which is what keeps the pair reading as one line and
    // its shadow rather than as two strokes drifting apart. It starts in open
    // space, clear of the left stem, and lands on the baseline 20 units inside
    // the corner, so the bottom right is a staggered pair while the top left
    // stays a single point.
    //
    // Its two ends are exactly the two lit points below; drawn any longer the
    // stroke overshoots them into the stem and the corner.
    strokes: ['M 4 0 L 4 175', 'M 4 0 L 123 175', 'M 31 69 L 103 175', 'M 123 0 L 123 175'],
    nodes: [
      [4, 0],
      [4, 175],
      [31, 69],
      [103, 175],
      [123, 0],
      [123, 175],
    ],
  },
  {
    id: 'D',
    width: 102,
    strokes: ['M 4 0 A 94 87.5 0 0 1 4 175', 'M 6 26 A 68 61.5 0 0 1 6 149'],
    nodes: [
      [4, 0],
      [4, 175],
      [6, 26],
      [6, 149],
    ],
  },
  {
    id: 'E',
    width: 112,
    strokes: ['M 4 0 L 4 175', 'M 4 0 L 108 0', 'M 4 175 L 108 175', 'M 30 88 L 91 88'],
    nodes: [
      [108, 0],
      [108, 175],
      [30, 88],
      [91, 88],
    ],
  },
  {
    id: 'R',
    width: 114,
    // A bowl and two legs, with no upright -- the same deconstruction the D
    // gets.
    //
    // The bowl stops the moment it closes, on the first leg it comes back to.
    // It does not carry on across to the second: a bar reaching under itself
    // to the far leg's top makes the curve read as running past the stroke it
    // is supposed to end on. So the inner leg simply starts in open space at
    // (28, 92), the way the N's adjacent line does, and only the outer leg
    // shares its top with the bowl.
    //
    // The bowl is a half-ellipse for the same reason the D's is: its endpoints
    // are pinned to one vertical, so the vertical radius is half the chord and
    // the horizontal one is whatever the letter is wide.
    strokes: ['M 4 0 L 56 0 A 54 46 0 0 1 56 92', 'M 28 92 L 73 170', 'M 56 92 L 105 175'],
    nodes: [
      [4, 0],
      [28, 92],
      [56, 92],
      [73, 170],
      [105, 175],
    ],
  },
];

/** Glyphs with their x position resolved from the widths that precede them. */
export const LETTERS = GLYPHS.reduce((placed, glyph) => {
  const previous = placed[placed.length - 1];
  const x = previous ? previous.x + previous.width + GAP : 0;
  return [...placed, { ...glyph, x }];
}, []);

const LAST = LETTERS[LETTERS.length - 1];
const WORD_WIDTH = LAST.x + LAST.width;

export const VIEWBOX = {
  x: -PAD,
  y: -PAD,
  width: WORD_WIDTH + PAD * 2,
  height: CAP + PAD * 2,
};

/** Every node in word coordinates, in draw order. */
export const WORDMARK_NODES = LETTERS.flatMap((letter, letterIndex) =>
  letter.nodes.map(([x, y], nodeIndex) => ({
    id: `${letter.id}-${nodeIndex}`,
    letterIndex,
    x: letter.x + x,
    y,
  })),
);

/** A node's position as CSS percentages of the rendered SVG box. */
export function atWordmark(node) {
  return {
    left: `${((node.x - VIEWBOX.x) / VIEWBOX.width) * 100}%`,
    top: `${((node.y - VIEWBOX.y) / VIEWBOX.height) * 100}%`,
  };
}

/**
 * Where a stroke flies in from before it lands in the word.
 *
 * The section's claim is that the word is assembled out of matches, so the
 * letters have to be seen arriving rather than switched on in place. Each
 * stroke is thrown outward along the axis it will come back on: the further a
 * letter sits from the centre of the word, the further out its strokes start,
 * so the whole thing collapses inward instead of sliding across.
 *
 * Offsets are in the same user units as the glyphs and are returned as a plain
 * translation. Nothing here rotates or scales -- a transform with an origin,
 * applied to an SVG element, is measured off `getBBox()` and does not reliably
 * resolve back to identity when the tween ends, which is what left an earlier
 * pass with every dot sitting a few units up and left of its stroke.
 *
 * Deterministic, so the word assembles the same way on every visit and the
 * markup can carry the values as attributes rather than the animation having
 * to invent them at build time.
 */
export function scatterFor(letter, strokeIndex) {
  const centre = VIEWBOX.x + VIEWBOX.width / 2;
  const fromCentre = letter.x + letter.width / 2 - centre;

  // The two terms do different jobs: the first spreads the letters apart, the
  // second stops the strokes *within* a letter travelling as one block.
  const x = fromCentre * 0.6 + (strokeIndex % 2 === 0 ? -38 : 38);
  const y = [-104, 96, -58, 132][strokeIndex % 4];

  return { x: Math.round(x), y };
}
